import React, { useState } from 'react';
import { interviewersAPI } from '../../utils/api';
import toastr from 'toastr';
import './EvaluationForm.css';

function EvaluationForm({ assessment, onSubmitted, onCancel }) {
  const [formData, setFormData] = useState({
    technicalScore: 5,
    problemSolvingScore: 5,
    communicationScore: 5,
    notes: '',
    recommendation: 'hire'
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleScoreChange = (field, value) => {
    const score = parseInt(value);
    if (score >= 1 && score <= 10) {
      setFormData(prev => ({ ...prev, [field]: score }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (formData.notes.trim().length < 20) {
      setError('Notes must be at least 20 characters long');
      return;
    }

    try {
      setSubmitting(true);
      await interviewersAPI.submitEvaluation(assessment._id, formData);
      toastr.success('Evaluation submitted successfully!');
      onSubmitted();
    } catch (error) {
      console.error('Failed to submit evaluation:', error);
      setError(error.response?.data?.error || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const getOverallScore = () => {
    return ((formData.technicalScore + formData.problemSolvingScore + formData.communicationScore) / 3).toFixed(1);
  };

  return (
    <div className="evaluation-form">
      <div className="form-header">
        <h2>Submit Evaluation</h2>
        <p className="form-subtitle">Candidate: {assessment.candidateId?.name} - Position: {assessment.position}</p>
      </div>

      {/* Assessment Summary */}
      <div className="assessment-summary">
        <h3>Assessment Summary</h3>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="label">Technical Score:</span>
            <span className="value">{assessment.results?.overallScore}%</span>
          </div>
          <div className="summary-item">
            <span className="label">Status:</span>
            <span className={`value ${assessment.results?.passed ? 'passed' : 'failed'}`}>
              {assessment.results?.passed ? 'PASSED' : 'FAILED'}
            </span>
          </div>
          <div className="summary-item">
            <span className="label">Flags Correct:</span>
            <span className="value">
              {assessment.results?.flagsCorrect} / {assessment.results?.flagsSubmitted}
            </span>
          </div>
          <div className="summary-item">
            <span className="label">Hints Used:</span>
            <span className="value">{assessment.results?.hintsUsed}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="eval-form">
        {/* Technical Score */}
        <div className="form-section">
          <label className="form-label">
            Technical Score (1-10)
            <span className="score-display">{formData.technicalScore}</span>
          </label>
          <div className="score-slider">
            <input
              type="range"
              min="1"
              max="10"
              value={formData.technicalScore}
              onChange={(e) => handleScoreChange('technicalScore', e.target.value)}
              className="slider"
            />
            <div className="score-labels">
              <span>Poor</span>
              <span>Average</span>
              <span>Excellent</span>
            </div>
          </div>
          <p className="score-description">
            Evaluate the candidate's technical knowledge and ability to apply security concepts.
          </p>
        </div>

        {/* Problem Solving Score */}
        <div className="form-section">
          <label className="form-label">
            Problem Solving Score (1-10)
            <span className="score-display">{formData.problemSolvingScore}</span>
          </label>
          <div className="score-slider">
            <input
              type="range"
              min="1"
              max="10"
              value={formData.problemSolvingScore}
              onChange={(e) => handleScoreChange('problemSolvingScore', e.target.value)}
              className="slider"
            />
            <div className="score-labels">
              <span>Poor</span>
              <span>Average</span>
              <span>Excellent</span>
            </div>
          </div>
          <p className="score-description">
            Rate the candidate's analytical thinking and approach to solving challenges.
          </p>
        </div>

        {/* Communication Score */}
        <div className="form-section">
          <label className="form-label">
            Communication Score (1-10)
            <span className="score-display">{formData.communicationScore}</span>
          </label>
          <div className="score-slider">
            <input
              type="range"
              min="1"
              max="10"
              value={formData.communicationScore}
              onChange={(e) => handleScoreChange('communicationScore', e.target.value)}
              className="slider"
            />
            <div className="score-labels">
              <span>Poor</span>
              <span>Average</span>
              <span>Excellent</span>
            </div>
          </div>
          <p className="score-description">
            Assess clarity of thought process and ability to explain technical concepts.
          </p>
        </div>

        {/* Overall Score Display */}
        <div className="overall-score">
          <span className="overall-label">Overall Score:</span>
          <span className="overall-value">{getOverallScore()} / 10</span>
        </div>

        {/* Notes */}
        <div className="form-section">
          <label className="form-label">
            Evaluation Notes
            <span className="char-count">
              {formData.notes.length} characters (minimum 20)
            </span>
          </label>
          <textarea
            className="form-textarea"
            rows="6"
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Provide detailed feedback on the candidate's performance, strengths, areas for improvement, and any notable observations during the assessment..."
            required
          />
        </div>

        {/* Recommendation */}
        <div className="form-section">
          <label className="form-label">Hiring Recommendation</label>
          <div className="recommendation-options">
            <label className="recommendation-option">
              <input
                type="radio"
                name="recommendation"
                value="strong_hire"
                checked={formData.recommendation === 'strong_hire'}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
              />
              <span className="option-label strong-hire">Strong Hire</span>
              <span className="option-description">Exceptional candidate, highly recommend</span>
            </label>

            <label className="recommendation-option">
              <input
                type="radio"
                name="recommendation"
                value="hire"
                checked={formData.recommendation === 'hire'}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
              />
              <span className="option-label hire">Hire</span>
              <span className="option-description">Good candidate, recommend for hire</span>
            </label>

            <label className="recommendation-option">
              <input
                type="radio"
                name="recommendation"
                value="no_hire"
                checked={formData.recommendation === 'no_hire'}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
              />
              <span className="option-label no-hire">No Hire</span>
              <span className="option-description">Does not meet requirements</span>
            </label>

            <label className="recommendation-option">
              <input
                type="radio"
                name="recommendation"
                value="strong_no_hire"
                checked={formData.recommendation === 'strong_no_hire'}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendation: e.target.value }))}
              />
              <span className="option-label strong-no-hire">Strong No Hire</span>
              <span className="option-description">Strongly not recommended</span>
            </label>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting || formData.notes.length < 20}
          >
            {submitting ? 'Submitting...' : 'Submit Evaluation'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EvaluationForm;
